/**
 * Music files by name. Anything dropped into `/music` is found by its file name alone, so adding
 * a track never needs a code change:
 *
 *   title                         the title screen and the menus behind it
 *   briefing                      the briefing
 *   <biome>-<time>                a mission: arctic | desert | temperate, then dawn | day | dusk | night
 *   <biome>                       any mission on that ground, when there is no track for the hour
 *   mission-<time>, mission       any mission at that hour / any mission at all
 *   mission-success, mission-fail the after-action report, played once (else `debrief`)
 *   memorial                      the memorial page
 *
 * Common misspellings are read as the word meant (`artic`, `breifing`), so nobody has to rename
 * a file. Where no file answers, the synthesised score plays as before. Pure: names and lookups.
 */

import type { Biome, TimeOfDay } from '../sim/types';

export const TITLE_TRACKS: readonly string[] = ['title'];
export const BRIEFING_TRACKS: readonly string[] = ['briefing'];
export const SUCCESS_TRACKS: readonly string[] = ['mission-success', 'success', 'debrief', 'aftermath'];
export const FAIL_TRACKS: readonly string[] = ['mission-fail', 'mission-failed', 'fail', 'debrief', 'aftermath'];
export const MEMORIAL_TRACKS: readonly string[] = ['memorial'];

/** Each word of a file name, as it is often typed -> as it is meant. */
const SPELLING: Readonly<Record<string, string>> = {
  artic: 'arctic', arctik: 'arctic', antarctic: 'arctic',
  dessert: 'desert',
  temprate: 'temperate', temperat: 'temperate', temperature: 'temperate',
  breifing: 'briefing', briefng: 'briefing',
  memorail: 'memorial',
  sucess: 'success', succes: 'success', failure: 'fail', failed: 'fail',
  nite: 'night',
};

/** Most specific first. */
export function missionTrackNames(biome: Biome, timeOfDay: TimeOfDay): string[] {
  return [`${biome}-${timeOfDay}`, biome, `mission-${timeOfDay}`, 'mission'];
}

/** `/music/Artic-Dusk.MP3` -> `arctic-dusk`: lower case, no extension, spaces and underscores as hyphens. */
export function trackName(path: string): string {
  const file = path.slice(path.lastIndexOf('/') + 1);
  const dot = file.lastIndexOf('.');
  const bare = (dot > 0 ? file.slice(0, dot) : file).trim().toLowerCase();
  return bare.split(/[\s_-]+/).filter((word) => word !== '').map((word) => SPELLING[word] ?? word).join('-');
}

/** Name -> address, from a map of path -> address (what `import.meta.glob` gives). */
export function trackTable(files: Readonly<Record<string, string>>): Map<string, string> {
  const table = new Map<string, string>();
  for (const [path, url] of Object.entries(files)) table.set(trackName(path), url);
  return table;
}

/** The first of `names` there is a file for. */
export function pickTrack(table: ReadonlyMap<string, string>, names: readonly string[]): { name: string; url: string } | null {
  for (const name of names) {
    const url = table.get(name);
    if (url !== undefined) return { name, url };
  }
  return null;
}
